import type { ReactNode } from "react";

import { Absent } from "./PhoneShell.tsx";

/**
 * The frame the five screens of `07 Payment Outcome Unknown.dc.html` are drawn in.
 *
 * A third shell, because the design file is a third shell: an app bar carrying the CityCab mark
 * alone, a tray on a tinted ground, and a footer. Every value in it is its own — the app bar's
 * control is a 36dp rounded square where the commerce files use a 38dp circle, the tray is a grid
 * aligned to the start where theirs is a flex column, and the footer's secondary button has a white
 * fill where theirs is transparent.
 *
 * The DOM below is the design's own, element for element and in its order:
 *
 *     phone outcome
 *       bar                   arrow_back, `Payment`, the subject, and the CityCab mark
 *       tray
 *         hero                a ring with a glyph, the state chip, the title and the sub
 *         instrument          which instrument, its reference, and the amount
 *         [dont]              `Don't pay again`, on the two uncertain states only
 *         blocks              what is known, what is not, and what happens next
 *         [checks]            how many times the provider has been asked
 *       footer                the 52dp buttons
 *
 * ## The one state in here that is a behaviour rather than a value
 *
 * The design's `Check again now` button has an `act` of `check`, and pressing it sets `checking` for
 * 1200ms: the label becomes `Checking…`, the glyph becomes `progress_activity`, and the line under
 * the blocks becomes `Asking your provider now…`. That is a designed state and it is built, driven
 * by the read actually being in flight rather than by a timer — which is the same state for a
 * shorter or longer time rather than a different one.
 *
 * **It is a read.** `CLAUDE.md` section 2: a "check status" action is a read, never a retry of the
 * command. The design says the same thing in its own notes — "It is safe to tap any number of
 * times" — and EXP-002's `payment-outcomes` is a `GET`. The one button on these screens that *is* a
 * command is `Try mobile money again` on `4b`, which the design draws only once the outcome is known
 * to be a failure, when paying again is safe.
 */

/** The four `K` kinds, which decide the hero's ring, its glyph colour and the chip's fill. */
export type OutcomeKind = "wait" | "unknown" | "done" | "failed";

/** The three block kinds, each with its own fill, border, heading colour and bullet. */
export type BlockKind = "known" | "unknown" | "next";

/** One line of a block. Either a sentence, or the reason there is no sentence. */
export interface BlockLine {
  readonly text?: string;
  readonly absent?: string;
}

export interface Block {
  readonly kind: BlockKind;
  /** The design's own heading per state, which is **not** fixed per kind: `prompt` writes
   * `What's true now` where `unknown` writes `What we know`, both over the `known` kind. */
  readonly heading: string;
  readonly lines: readonly BlockLine[];
}

export interface OutcomeAction {
  readonly label: string;
  readonly glyph: string;
  readonly primary: boolean;
  readonly onPress?: () => void;
  readonly unavailable?: string;
}

export interface OutcomeShellProps {
  readonly screen: string;
  readonly onBack?: () => void;
  /** `Trip trp_8F2K · Westlands → Kilimani`, or the reason the subject cannot be named. */
  readonly subject?: string;
  readonly subjectAbsent?: string;
  readonly kind: OutcomeKind;
  readonly heroGlyph: string;
  readonly chip: string;
  readonly title: string;
  readonly sub: string;
  /** Said under the sub when the words are this build's rather than the design's. */
  readonly flag?: string;
  readonly instrument: InstrumentValue;
  /** `Don't pay again. You won't be charged twice for this trip.` — the two uncertain states only. */
  readonly dont?: string;
  readonly blocks: readonly Block[];
  /** `Asked your provider 3 times · last at 21:15:40`, or the reason there is no count. */
  readonly checks?: string;
  readonly checksAbsent?: string;
  readonly actions: readonly OutcomeAction[];
}

/** The design's `Payment` — the one word in this file's app bar, and it is a literal there. */
const BAR_TITLE = "Payment";

export function OutcomeShell(props: OutcomeShellProps): ReactNode {
  return (
    <div className={`phone outcome ${props.screen}`}>
      <div className="bar">
        <button type="button" className="bar__icon" aria-label="Back" onClick={props.onBack}>
          <span className="glyph" aria-hidden="true">
            arrow_back
          </span>
        </button>

        <span className="bar__headings">
          <strong className="bar__title">{BAR_TITLE}</strong>
          <span className="bar__sub">
            {props.subjectAbsent === undefined ? (
              props.subject
            ) : (
              <Absent words={props.subjectAbsent} />
            )}
          </span>
        </span>

        <span className="bar__mark" aria-label="CityCab" role="img" />
      </div>

      <div className="tray">
        <div className={`verdict verdict--${props.kind}`}>
          <span className="verdict__ring">
            <span className="verdict__glyph glyph" aria-hidden="true">
              {props.heroGlyph}
            </span>
          </span>
          <span className="verdict__chip">
            <span className="verdict__chip-glyph" aria-hidden="true">
              {props.kind === "unknown" ? "?" : ""}
            </span>
            {props.chip}
          </span>
          <strong className="verdict__title">{props.title}</strong>
          <span className="verdict__sub">{props.sub}</span>
          {props.flag === undefined ? null : <span className="verdict__sub">{props.flag}</span>}
        </div>

        <Instrument {...props.instrument} />

        {props.dont === undefined ? null : (
          <div className="dont" role="alert">
            <span className="glyph" aria-hidden="true">
              front_hand
            </span>
            <span className="dont__text">{props.dont}</span>
          </div>
        )}

        {props.blocks.map((block) => (
          <BlockCard key={block.heading} {...block} />
        ))}

        {props.checks === undefined && props.checksAbsent === undefined ? null : (
          <span className="checks">
            {props.checksAbsent === undefined ? (
              props.checks
            ) : (
              <Absent words={props.checksAbsent} />
            )}
          </span>
        )}
      </div>

      <div className="footer">
        {props.actions.map((action) => (
          <button
            key={action.label}
            type="button"
            className={`action${action.primary ? " action--primary" : ""}`}
            {...(action.unavailable === undefined
              ? {}
              : { "aria-disabled": true, title: action.unavailable })}
            onClick={action.unavailable === undefined ? action.onPress : undefined}
          >
            <span className="glyph" aria-hidden="true">
              {action.glyph}
            </span>
            {action.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * The instrument card, which is the one card on this screen whose middle has no source.
 *
 * The design writes `Mobile money ··· 0421`. EXP-002 publishes `method` — an enumeration of three —
 * and `instrument: null` with `instrumentAbsence: no_published_service`, because **no service on
 * this platform models a stored payment instrument**. So the rail is named from what is published
 * and the four digits are drawn as the absence beside it, rather than the whole row being dropped or
 * a plausible masked number being written into a payment receipt.
 */
export interface InstrumentValue {
  readonly glyph: string;
  readonly name?: string;
  readonly nameAbsent?: string;
  /** The masked digits, which have no source. Drawn **beside** the rail, on the design's one line. */
  readonly maskAbsent?: string;
  readonly reference?: string;
  readonly referenceAbsent?: string;
  readonly amount?: string;
  readonly amountAbsent?: string;
}

function Instrument(value: InstrumentValue): ReactNode {
  return (
    <div className="instrument">
      <span className="instrument__glyph glyph" aria-hidden="true">
        {value.glyph}
      </span>
      <span className="instrument__text">
        <span className="instrument__name">
          {value.nameAbsent === undefined ? value.name : <Absent words={value.nameAbsent} />}
          {value.maskAbsent === undefined ? null : <Absent words={value.maskAbsent} />}
        </span>
        <span className="instrument__reference">
          {value.referenceAbsent === undefined ? (
            value.reference
          ) : (
            <Absent words={value.referenceAbsent} />
          )}
        </span>
      </span>
      <span className="instrument__amount">
        {value.amountAbsent === undefined ? value.amount : <Absent words={value.amountAbsent} />}
      </span>
    </div>
  );
}

/** The bullet glyph per block kind: `check`, nothing, and `arrow_forward`. */
const BULLETS: Readonly<Record<BlockKind, string>> = {
  known: "check",
  unknown: "",
  next: "arrow_forward",
};

function BlockCard({ kind, heading, lines }: Block): ReactNode {
  return (
    <div className={`block block--${kind}`}>
      <span className="block__head">
        {kind === "unknown" ? (
          <span className="block__mark" aria-hidden="true">
            ?
          </span>
        ) : null}
        {heading}
      </span>
      {lines.map((line, index) => (
        <span className="block__item" key={line.text ?? `absent-${String(index)}`}>
          <span className="block__bullet glyph" aria-hidden="true">
            {BULLETS[kind]}
          </span>
          {line.absent === undefined ? line.text : <Absent words={line.absent} />}
        </span>
      ))}
    </div>
  );
}
